<script>
import { signIn } from '@/api/auth.js'
import { saveTokens } from '@/utils/token.js'

/** API 오류 코드별 사용자 메시지 */
const ERROR_MESSAGES = {
  1000: '등록되지 않은 계정입니다.',
  1001: '비활성화된 계정입니다. 관리자에게 문의해 주세요.',
  1002: '지점 코드를 입력해 주세요.',
  1003: '지점 코드 또는 계정 정보를 확인해 주세요.',
  1004: '지점타입이 올바르지 않습니다.',
  10020: '비밀번호가 올바르지 않습니다.',
  10100: '입력 정보를 다시 확인해 주세요.',
}

/** 지점타입 옵션 (branch.branch_type) */
const BRANCH_TYPE_OPTIONS = [
  { value: 'HQ', title: '본사' },
  { value: 'DIRECT', title: '직영점' },
  { value: 'AGENCY', title: '대리점' },
  { value: 'FRANCHISE', title: '가맹점' },
]

export default {
  name: 'LoginPage',
  data() {
    return {
      /** 선택된 지점타입 (HQ | DIRECT | AGENCY | FRANCHISE) */
      branchType: 'HQ',
      form: {
        loginId: '',
        password: '',
        remember: false,
      },
      isPasswordVisible: false,
      loading: false,
      errorMsg: '',
    }
  },
  computed: {
    branchTypeOptions() {
      return BRANCH_TYPE_OPTIONS
    },
  },
  watch: {
    branchType() {
      this.errorMsg = ''
    },
  },
  methods: {
    async submit() {
      this.errorMsg = ''

      if (!this.branchType) {
        this.errorMsg = '지점타입을 선택해 주세요.'
        return
      }
      if (!this.form.loginId.trim()) {
        this.errorMsg = '아이디를 입력해 주세요.'
        return
      }
      if (!this.form.password) {
        this.errorMsg = '비밀번호를 입력해 주세요.'
        return
      }

      this.loading = true
      try {
        const { data } = await signIn({
          branchType: this.branchType,
          loginId: this.form.loginId.trim(),
          password: this.form.password,
        })

        if (data.code === 0) {
          saveTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            adminCode: data.adminCode,
            adminName: data.adminName,
            adminType: data.adminType,
            branchCode: data.branchCode,
          })
          if (this.form.remember) {
            localStorage.setItem('savedLoginId', this.form.loginId)
            localStorage.setItem('savedBranchType', this.branchType)
          } else {
            localStorage.removeItem('savedLoginId')
            localStorage.removeItem('savedBranchType')
          }
          this.$router.push('/dashboard')
        } else {
          this.errorMsg = ERROR_MESSAGES[data.code] || data.message || '로그인에 실패했습니다.'
        }
      } catch (err) {
        if (err.response?.data?.message) {
          this.errorMsg = err.response.data.message
        } else {
          this.errorMsg = '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.'
        }
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    const savedId = localStorage.getItem('savedLoginId')
    const savedBranchType = localStorage.getItem('savedBranchType')
    if (savedId) {
      this.form.loginId = savedId
      this.form.remember = true
    }
    if (savedBranchType && BRANCH_TYPE_OPTIONS.some(o => o.value === savedBranchType)) {
      this.branchType = savedBranchType
    }
  },
}
</script>

<template>
  <div class="auth-wrapper">
    <v-card class="auth-card" max-width="460" elevation="0">
      <v-card-text class="px-8 pt-8 pb-8">
        <div class="auth-head">
          <h1 class="auth-brand">GOLDNARA</h1>
          <p class="auth-caption">관리자 로그인</p>
        </div>

        <v-form @submit.prevent="submit">
          <div class="auth-form-grid">
            <v-select
              v-model="branchType"
              :items="branchTypeOptions"
              item-title="title"
              item-value="value"
              label="지점타입"
              class="auth-field"
              variant="outlined"
              density="compact"
              hide-details="auto"
            />

            <v-text-field
              v-model="form.loginId"
              label="아이디"
              placeholder="아이디를 입력"
              class="auth-field"
              variant="outlined"
              density="compact"
              autofocus
              hide-details="auto"
            />

            <v-text-field
              v-model="form.password"
              label="비밀번호"
              placeholder="비밀번호를 입력"
              class="auth-field"
              :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              :type="isPasswordVisible ? 'text' : 'password'"
              autocomplete="current-password"
              variant="outlined"
              density="compact"
              hide-details="auto"
              @click:append-inner="isPasswordVisible = !isPasswordVisible"
            />

            <v-checkbox
              v-model="form.remember"
              class="auth-remember"
              label="아이디 저장"
              density="compact"
              hide-details
              color="primary"
            />

            <v-alert
              v-if="errorMsg"
              class="auth-error"
              type="error"
              variant="tonal"
              density="compact"
              :text="errorMsg"
            />

            <v-btn
              block
              color="primary"
              class="auth-submit-btn"
              size="large"
              type="submit"
              :loading="loading"
            >
              로그인
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 14% 18%, rgba(47, 63, 104, 0.24), transparent 34%),
    radial-gradient(circle at 82% 86%, rgba(18, 28, 54, 0.24), transparent 36%),
    #0d1018;
  padding: 20px 16px;
}

.auth-card {
  border: 1px solid rgba(119, 126, 146, 0.22);
  border-radius: 10px !important;
  background: rgba(36, 38, 45, 0.92);
  box-shadow: 0 18px 40px rgba(3, 6, 16, 0.38);
  width: 100%;
}

.auth-head {
  text-align: center;
  margin-bottom: 14px;
}

.auth-brand {
  margin: 0;
  font-size: 30px;
  line-height: 1.1;
  letter-spacing: 0.03em;
  font-weight: 700;
  color: #c7a967;
}

.auth-caption {
  margin: 8px 0 0;
  font-size: 13px;
  color: rgba(236, 229, 209, 0.72);
}

.auth-form-grid {
  display: grid;
  gap: 14px;
}

.auth-submit-btn {
  min-height: 44px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  color: #171717 !important;
}

.auth-error {
  font-size: 12px;
}

.auth-remember :deep(.v-label) {
  color: #cbd5e1;
  font-size: 12px;
}

:deep(.auth-field .v-label) {
  color: rgba(198, 205, 220, 0.74) !important;
  font-size: 11px;
}

:deep(.auth-field .v-field) {
  border-radius: 8px !important;
  background: #14161d;
  border-color: rgba(98, 107, 129, 0.35) !important;
  min-height: 44px;
}

:deep(.auth-field .v-field__input) {
  min-height: 44px !important;
  color: #f4f6fb;
  font-size: 14px;
}

:deep(.auth-field .v-field__append-inner .v-icon),
:deep(.auth-field .v-field__prepend-inner .v-icon),
:deep(.auth-field .v-select__menu-icon) {
  color: rgba(198, 205, 220, 0.74) !important;
}

:deep(.auth-field input::placeholder) {
  color: rgba(157, 167, 190, 0.72) !important;
}
</style>
